/**
 * Shared vocabulary for the onboarding questionnaire and the customer-discovery
 * survey, used by the "by age" charts on User Management and Survey Tracking.
 *
 * Two separate products asked two overlapping questions with two different word
 * lists, so each has its own age ordering and its own theme grouping. Both
 * collapse to the SAME four themes so the charts read alike.
 *
 * Single source of truth: change a grouping here and both charts follow.
 */

import { brandColors } from '../../theme/brandColors';

/** Value -> member-facing label for every onboarding V2 answer. */
export const ONBOARDING_LABELS: Record<string, string> = {
  self: 'Preserve my own story',
  gift_giver: 'For someone I love',
  family: 'Doing this together',
  urgency: 'Capturing stories only I can tell',
  grandchildren: 'For my grandchildren',
  photos: 'Old photos into something beautiful',
  brain_health: 'Mental engagement',
  capture_stories: 'Capture stories before gone',
  meaningful_gift: 'Most meaningful gift ever',
  family_connection: 'Family feels more connected',
  make_it_easy: 'Make it easy for them',
  print: 'Printed Life Book',
  digital: 'Private digital collection',
  movies_plus: 'Both + Life Movies',
  full_legacy: 'Complete family legacy',
  under_45: 'Under 45',
  '45_54': '45-54',
  '55_64': '55-64',
  '65_74': '65-74',
  '75_plus': '75+',
};

export interface ReasonTheme {
  /** Stable key — also the Recharts dataKey for the stacked segment. */
  key: string;
  label: string;
  color: string;
  /** Raw answer values that roll up into this theme. */
  values: string[];
}

/**
 * Onboarding motivation (8 values) -> 4 themes.
 *
 * The 8 values are two disjoint sets branched on onboarding_identity:
 * self/family members see urgency|grandchildren|photos|brain_health, gift
 * givers see capture_stories|meaningful_gift|family_connection|make_it_easy.
 * Each gift-giver value is grouped with its nearest self/family counterpart.
 */
export const APP_THEMES: ReasonTheme[] = [
  {
    key: 'own_stories',
    label: 'Stories only I can tell',
    color: brandColors.deepTeal,
    values: ['urgency', 'capture_stories'],
  },
  {
    key: 'family_legacy',
    label: 'A legacy for family',
    color: brandColors.warmGold,
    values: ['grandchildren', 'family_connection', 'meaningful_gift'],
  },
  {
    key: 'photos',
    label: 'Photos into something beautiful',
    color: brandColors.forestGreen,
    values: ['photos'],
  },
  {
    key: 'engagement',
    label: 'Mental engagement & ease',
    color: brandColors.richBurgundy,
    values: ['brain_health', 'make_it_easy'],
  },
];

/** Survey q2_reason_tried (6 values) -> the same four themes. */
export const SURVEY_THEMES: ReasonTheme[] = [
  {
    key: 'family_legacy',
    label: 'A legacy for family',
    color: brandColors.warmGold,
    values: ['preserve_for_family', 'leave_meaningful'],
  },
  {
    key: 'own_stories',
    label: 'Tell my own story',
    color: brandColors.deepTeal,
    values: ['tell_my_story'],
  },
  {
    key: 'curious_ai',
    label: 'Curious about AI',
    color: brandColors.forestGreen,
    values: ['curious_ai'],
  },
  {
    key: 'word_of_mouth',
    label: 'Recommended / other',
    color: brandColors.richBurgundy,
    values: ['recommended', 'other'],
  },
];

/** Age buckets in display order. The two products cut them differently. */
export const APP_AGE_ORDER = ['under_45', '45_54', '55_64', '65_74', '75_plus'];

export const SURVEY_AGE_ORDER = [
  'under_35',
  '35_44',
  '45_54',
  '55_64',
  '65_74',
  '75_plus',
];

export const SURVEY_AGE_LABELS: Record<string, string> = {
  under_35: 'Under 35',
  '35_44': '35-44',
  '45_54': '45-54',
  '55_64': '55-64',
  '65_74': '65-74',
  '75_plus': '75+',
};

/** Bucket for blank answers and prefer_not_to_say — shown, never silently dropped. */
export const NOT_GIVEN_KEY = '__not_given__';
export const NOT_GIVEN_LABEL = 'Not given';

/** Maps one raw answer value to its theme key, or null if unrecognised. */
export function themeKeyFor(themes: ReasonTheme[], value: string): string | null {
  const match = themes.find((t) => t.values.includes(value));
  return match ? match.key : null;
}
